import moment from 'moment-timezone'
import Flatpickr from 'react-flatpickr'
import { Calendar } from 'react-feather'
import PMBInputNumber from 'components/PMBInputNumber'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap'
import { formatInputNumber, parseFormattedNumber } from 'utility/Utils'
import {
  setEmployeeEdit,
  setIsOpenModalManual,
  setManualDisbursement,
} from './employeePaymentsListSlice'

const DisbursementModal = ({ isOpen, employee = {}, showDateInput = true }) => {
  const amountInputRef = useRef(null)
  const dispatch = useDispatch()
  const [formData, setFormData] = useState({
    amount: 0,
    notes: '',
    disburseMethod: 'CASH',
    createdAt: new Date(),
  })

  const [errors, setErrors] = useState({})

  const paymentMethods = [
    {
      value: 'TRANSFER',
      label: 'Bank Transfer',
      description: 'Direct bank transfer',
      disabled: true,
    },
    {
      value: 'AGENT',
      label: 'Payment By Agent',
      description: 'Payment through designated agent',
      disabled: true,
    },
    {
      value: 'CASH',
      label: 'Tunai',
      description: 'Manual cash payment',
      disabled: false,
    },
  ]

  useEffect(() => {
    if (isOpen) {
      setFormData({
        amount: employee.amount || 0,
        notes: employee.notes || '',
        disburseMethod: employee.disburseMethod || 'CASH',
        createdAt: employee.createdAt
          ? new Date(employee.createdAt)
          : new Date(),
      })
      setErrors({})
    }
  }, [isOpen, employee?.employeeUserId])

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (showDateInput && !formData.createdAt) {
      newErrors.createdAt = 'Date is required'
    }

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0'
    }

    if (!formData.notes || formData.notes.trim() === '') {
      newErrors.notes = 'Notes is required'
    }

    if (!formData.disburseMethod) {
      newErrors.disburseMethod = 'Payment method is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (validateForm()) {
      const createdAtIso = formData.createdAt
        ? (formData.createdAt instanceof Date
            ? formData.createdAt.toISOString()
            : new Date(formData.createdAt).toISOString())
        : new Date().toISOString()

      dispatch(
        setManualDisbursement({
          employeeUserId: employee.employeeUserId,
          employeeName: employee.employeeName,
          amount: Number(formData.amount),
          notes: formData.notes.trim(),
          disburseMethod: formData.disburseMethod,
          createdAt: createdAtIso,
        }),
      )

      // RESET
      toggleModalManual()
    }
  }

  const handleAmountChange = useCallback((value) => {
    const numericValue = parseFormattedNumber(value)

    handleChange('amount', numericValue)
  }, [])

  const toggleModalManual = () => {
    dispatch(setIsOpenModalManual(false))
    dispatch(setEmployeeEdit(null))
    setFormData({
      amount: 0,
      notes: '',
      disburseMethod: 'CASH',
      createdAt: new Date(),
    })
  }

  const handlePaymentMethodClick = (method) => {
    if (!method.disabled) {
      handleChange('disburseMethod', method.value)
    }
  }

  if (!employee) return null

  return (
    <Modal
      className="modal-lg modal-dialog-centered"
      placement="top"
      target="center"
      size="lg"
      isOpen={isOpen}
      toggle={toggleModalManual}
      data-testid="emp-pay-modal-manual"
      onOpened={() => {
        setTimeout(() => {
          if (amountInputRef.current) {
            amountInputRef.current.focus()
          }
        }, 100)
      }}
    >
      <ModalHeader toggle={toggleModalManual}>
        Disbursement for {employee?.employeeName || ''}
      </ModalHeader>
      <Form onSubmit={handleSubmit} className="m-4">
        <ModalBody
          style={{
            maxHeight: '70vh',
            overflowY: 'auto',
            padding: '1.5rem',
          }}
        >
          <Row>
            <Col md={12}>
              <div className="mb-3 p-3 bg-light rounded">
                <Row>
                  <Col md={6}>
                    <p className="mb-1 text-muted small">Employee Name</p>
                    <p className="mb-0 fw-semibold">{employee?.employeeName}</p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1 text-muted small">Role</p>
                    <p className="mb-0 fw-semibold">{employee?.rolesName}</p>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          {showDateInput && (
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="createdAt">
                    Date <span className="text-danger">*</span>
                  </Label>
                  <div className="d-flex align-items-center border rounded pe-2 bg-white" style={{ minHeight: '40px' }}>
                    <Calendar size={18} className="ms-3 text-muted" />
                    <Flatpickr
                      id="createdAt"
                      name="createdAt"
                      value={formData.createdAt}
                      onChange={([date]) => handleChange('createdAt', date)}
                      options={{
                        altInput: true,
                        altFormat: 'j F Y',
                        dateFormat: 'Y-m-d',
                      }}
                      className="form-control bg-transparent border-0 shadow-none"
                    />
                  </div>
                  {errors.createdAt && (
                    <div className="text-danger small mt-1">{errors.createdAt}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          )}

          <Row>
            <Col md={12}>
              <FormGroup>
                <Label for="amount">
                  Amount <span className="text-danger">*</span>
                </Label>
                <PMBInputNumber
                  onFocus={(e) => e.target.select()}
                  name="amount"
                  value={formatInputNumber(formData.amount)}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder="Input amount"
                  prefix="Rp"
                  isFirstPrefix
                  min={1}
                  invalid={!!errors.amount}
                  autoFocus={true}
                  innerRef={amountInputRef}
                />
                <FormFeedback>{errors.amount}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <FormGroup>
                <Label for="notes">
                  Notes <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  id="notes"
                  placeholder="Enter notes or description"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  invalid={!!errors.notes}
                  rows={4}
                />
                <FormFeedback>{errors.notes}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <FormGroup>
                <Label>
                  Payment Method <span className="text-danger">*</span>
                </Label>
                <Row className="g-3">
                  {paymentMethods.map((method) => (
                    <Col md={4} key={method.value}>
                      <Card
                        className={`mb-0 ${method.disabled
                            ? 'border-secondary'
                            : 'border-primary cursor-pointer'
                          } ${formData.disburseMethod === method.value &&
                            !method.disabled
                            ? 'border-primary bg-primary bg-opacity-10'
                            : ''
                          }`}
                        style={{
                          cursor: method.disabled ? 'not-allowed' : 'pointer',
                          opacity: method.disabled ? 0.5 : 1,
                          transition: 'all 0.2s ease',
                          borderWidth: '1px',
                        }}
                        onClick={() => handlePaymentMethodClick(method)}
                      >
                        <CardBody className="text-center py-3">
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <Input
                              type="radio"
                              name="paymentMethod"
                              checked={formData.disburseMethod === method.value}
                              disabled={method.disabled}
                              onChange={() => handlePaymentMethodClick(method)}
                              style={{
                                cursor: method.disabled
                                  ? 'not-allowed'
                                  : 'pointer',
                                width: '14px',
                                height: '14px',
                              }}
                              className="m-0"
                            />
                            <div className="fw-semibold small">
                              {method.label}
                            </div>
                          </div>
                          <div
                            className="text-muted mt-1"
                            style={{ fontSize: '0.7rem' }}
                          >
                            {method.description}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
                {errors.disburseMethod && (
                  <div className="text-danger small mt-2">
                    {errors.disburseMethod}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="flex-nowrap" style={{ border: 0 }}>
          <Button
            className="btn-cancel"
            outline
            onClick={toggleModalManual}
            block
            data-testid="emp-pay-modal-manual-cancel"
          >
            Cancel
          </Button>
          <Button color="primary" type="submit" block data-testid="emp-pay-modal-manual-submit">
            Save
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default DisbursementModal
